// src/components/TeamNoteModal.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * TeamNoteModal
 * -------------
 * Kirjautumisen jälkeen aukeava modal, jolla voi jättää terveisiä / kuvata mitä on tehty.
 * Tallennus: WordPress REST API (CPT: team_note).
 *
 * Edellytykset WP-puolella:
 * - CPT 'team_note' show_in_rest=true
 * - wp_localize_script -> window.TEAM_NOTES_CTX = { wpApiNonce, currentUser:{id,name}, restBase }
 *
 * Käyttö:
 * <TeamNoteModal />
 *
 * Avauslogiikka:
 * - Aukeaa automaattisesti, jos URL sisältää ?just_logged_in=1
 * - Tai jos kutsut <TeamNoteModal forceOpen />
 */

export default function TeamNoteModal({ forceOpen = false }) {
  const location = useLocation();
  const navigate = useNavigate();

  // WP-konteksti windowista (asetettu functions.php:ssa)
  const ctx = useMemo(() => {
    if (typeof window !== "undefined" && window.TEAM_NOTES_CTX) {
      return window.TEAM_NOTES_CTX;
    }
    return { wpApiNonce: "", currentUser: { id: 0, name: "" }, restBase: "/wp-json/" };
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [noteType, setNoteType] = useState("terveinen"); // terveinen | tehty | huomio
  const [recent, setRecent] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Avaa automaattisesti jos just_logged_in=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const auto = params.get("just_logged_in") === "1";
    if (auto || forceOpen) setIsOpen(true);

    // Siisti osoite pois paramista kun modal suljetaan
    // (tämä tehdään sulkiessa)
  }, [location.search, forceOpen]);

  // Lataa viimeisimmät merkinnät
  useEffect(() => {
    if (!isOpen) return;
    void fetchRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  async function fetchRecent() {
    setLoadingList(true);
    setError("");
    try {
      // Hakee viimeiset 10 muistiinpanoa (kirjautuneelle käyttäjälle).
      // WP REST ei tue 'author=me', joten käytetään localisoitua currentUser.id:tä.
      const url = new URL(`${ctx.restBase}wp/v2/team_note`);
      url.searchParams.set("per_page", "10");
      if (ctx?.currentUser?.id) url.searchParams.set("author", String(ctx.currentUser.id));
      url.searchParams.set("_fields", "id,date,title,content");

      const res = await fetch(url.toString(), {
        credentials: "include",
        headers: { "X-WP-Nonce": ctx.wpApiNonce },
      });
      if (!res.ok) throw new Error(`List fetch failed: ${res.status}`);
      const data = await res.json();
      setRecent(
        data.map((p) => ({
          id: p.id,
          date: p.date,
          title: p.title?.rendered || "",
          // Content tulee renderöitynä. Näytetään se turvallisesti "dangerouslySetInnerHTML":llä
          content: p.content?.rendered || "",
        }))
      );
    } catch (e) {
      setError(e.message || "Virhe haettaessa listaa");
    } finally {
      setLoadingList(false);
    }
  }

  function closeModal() {
    setIsOpen(false);
    // Poista just_logged_in parametri siististi
    const params = new URLSearchParams(location.search);
    if (params.has("just_logged_in")) {
      params.delete("just_logged_in");
      navigate({ search: params.toString() }, { replace: true });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!message.trim()) {
      setError("Kirjoita viesti ennen tallennusta.");
      return;
    }

    setSaving(true);
    try {
      // Rakennetaan otsikko nopeaa selailua varten
      const date = new Date();
      const nice = date.toLocaleString("fi-FI");
      const title = `${noteType === "terveinen" ? "Terveiset" : noteType === "tehty" ? "Tein" : "Huomio"} – ${nice}`;

      // 1) luodaan post (team_note)
      const createRes = await fetch(`${ctx.restBase}wp/v2/team_note`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": ctx.wpApiNonce, // WP REST vaatii tämän
        },
        body: JSON.stringify({
          status: "publish",       // tai 'private' jos haluat piilottaa WP-listauksista
          title,
          content: message,
        }),
      });
      if (!createRes.ok) {
        const t = await createRes.text();
        throw new Error(`Tallennus epäonnistui (${createRes.status}): ${t}`);
      }
      const created = await createRes.json();

      // 2) (valinnainen) liitä taksonomia note_type
      //    vain jos taksonomia on otettu käyttöön WP:ssä
      try {
        await fetch(`${ctx.restBase}wp/v2/team_note/${created.id}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": ctx.wpApiNonce,
          },
          body: JSON.stringify({
            note_type: [noteType], // WP luo termit automaattisesti 'show_in_rest' päällä
          }),
        });
      } catch {
        // hiljainen: taksonomia on vapaaehtoinen
      }

      // Tyhjennä lomake, päivitä lista
      setMessage("");
      await fetchRecent();
    } catch (e) {
      setError(e.message || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Taustan himmennys */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeModal}
        aria-hidden="true"
      />
      {/* Itse modal */}
      <div className="relative w-[95%] max-w-2xl rounded-2xl bg-white p-6 shadow-2xl dark:bg-neutral-900 dark:text-neutral-100">
        <button
          onClick={closeModal}
          className="absolute right-3 top-3 rounded-full px-2 py-1 text-sm hover:bg-black/10 dark:hover:bg-white/10"
          aria-label="Sulje"
        >
          ✕
        </button>

        <h2 className="mb-1 text-xl font-semibold">
          Hei {ctx?.currentUser?.name || "kollega"} 👋
        </h2>
        <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-300">
          Jätä nopeat terveiset tai kirjaa mitä teit sivuston eteen. Nämä tallentuvat WordPressiin (vain kirjautuneille).
        </p>

        {error && (
          <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-600 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Tyyppi */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Tyyppi</label>
            <select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              className="rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-neutral-700 dark:bg-neutral-800"
            >
              <option value="terveinen">Terveiset</option>
              <option value="tehty">Mitä tein</option>
              <option value="huomio">Huomio</option>
            </select>
          </div>

          {/* Viesti */}
          <div>
            <label className="mb-1 block text-sm font-medium">Viesti</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-neutral-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-neutral-700 dark:bg-neutral-800"
              placeholder="Kirjoita terveisesi tai lyhyt kuvaus esim. 'Lisäsin meta-tägien og:image-logiikan ja korjasin Bookkauslomakkeen resetoinnin.'"
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl px-4 py-2 text-sm font-semibold shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 dark:bg-indigo-500 dark:text-white"
              style={{ background: "linear-gradient(90deg, #6366f1, #8b5cf6)", color: "#fff" }}
            >
              {saving ? "Tallennetaan…" : "Tallenna"}
            </button>

            <button
              type="button"
              onClick={closeModal}
              className="rounded-xl border border-neutral-300 px-3 py-2 text-sm hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              Sulje
            </button>
          </div>
        </form>

        {/* Viimeisimmät merkinnät */}
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold">Viimeisimmät merkintäsi</h3>
          {loadingList ? (
            <p className="text-sm text-neutral-500">Ladataan…</p>
          ) : recent.length === 0 ? (
            <p className="text-sm text-neutral-500">Ei merkintöjä vielä.</p>
          ) : (
            <ul className="space-y-2">
              {recent.map((r) => (
                <li key={r.id} className="rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800">
                  <div className="mb-1 text-[12px] text-neutral-500">{new Date(r.date).toLocaleString("fi-FI")}</div>
                  {/* renderöity sisältö (WP REST palauttaa HTML:n) */}
                  <div dangerouslySetInnerHTML={{ __html: r.content }} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
